import type { QueryResult } from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import type { ContextType, ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import toTopicTreeList, { flattenTopicTreeList } from './toTopicTreeList';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { useAllGroupMap } from '../groups/useAllGroups';
import { useAllUsersMap } from '../user/useAllUsers';
import updateApolloList from '../utils/graphql/updateApolloList';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import omitNonTopics from '../utils/omitNonTopics';
import populateObjects from '../utils/populateObjects';
import useIsPublicView from '../utils/useIsPublicView';
import sortTopic from './utils/sortTopic';

// eslint-disable-next-line slab-graphql/no-unused-fields
const ALL_TOPIC_FIELDS_FRAGMENT = gql(`
  fragment AllTopicFields on RedactedTopicFields {
    id
    inheritParent
    privacy
    parent {
      id
    }
    ... on Topic {
      name
      description
      score
      order
      memberEditable
      detached
      membership {
        ownerUserIds
        memberUserIds
        requestorUserIds
        ownerGroupIds
        memberGroupIds
      }
    }
    # Do not add inheritedParents here, toTopicTreeList will construct it
    ...toTopicTreeList_topic
  }
`);

const ALL_TOPICS_QUERY = gql(/** @complexity 66 */ `
  query allTopics {
    session: currentSession {
      organization {
        id
        topics {
          id
          ...AllTopicFields
        }
      }
    }
  }
`);

type AllTopicsTopic = Extract<
  DocumentType<typeof ALL_TOPIC_FIELDS_FRAGMENT>,
  { __typename?: 'Topic' }
>;

const TOPIC_CREATED_SUBSCRIPTION = gql(/** @complexity 16 */ `
  subscription topicCreated {
    topic: topicCreated {
      ...AllTopicFields
    }
  }
`);

const TOPIC_DELETED_SUBSCRIPTION = gql(/** @complexity 8 */ `
  subscription topicDeleted {
    topic: topicDeleted {
      id
      parent {
        id
      }
    }
  }
`);

const TOPIC_UPDATED_SUBSCRIPTION = gql(/** @complexity 16 */ `
  subscription topicUpdated {
    topic: topicUpdated {
      ...AllTopicFields
    }
  }
`);

const populateTopic = <
  Topic extends Pick<AllTopicsTopic, 'membership'>,
  Group,
  User,
>({
  topics,
  groupMap,
  userMap,
}: {
  topics: Topic[];
  groupMap: { [id: string]: Group };
  userMap: { [id: string]: User };
}) =>
  topics.map((topic) => {
    const {
      memberUserIds,
      ownerUserIds,
      memberGroupIds,
      ownerGroupIds,
      requestorUserIds,
    } = topic.membership || {};

    return {
      ...topic,
      members: populateObjects(userMap, memberUserIds),
      owners: populateObjects(userMap, ownerUserIds),
      requestors: populateObjects(userMap, requestorUserIds),
      ownerGroups: populateObjects(groupMap, ownerGroupIds),
      memberGroups: populateObjects(groupMap, memberGroupIds),
    };
  });

const useSubscribeEffect = (
  subscribeToMore: QueryResult<
    DocumentType<typeof ALL_TOPICS_QUERY>
  >['subscribeToMore'],
  skip: boolean,
) => {
  useEffect(() => {
    if (skip) return undefined;

    const unsubscribes = [
      TOPIC_CREATED_SUBSCRIPTION,
      TOPIC_DELETED_SUBSCRIPTION,
      // TODO updating color/parent can change other topics but Apollo currently
      // has a refetch cache bug: https://github.com/apollographql/react-apollo/issues/556
      TOPIC_UPDATED_SUBSCRIPTION,
    ].map((query) => {
      const path = 'session.organization.topics';

      if (query === TOPIC_DELETED_SUBSCRIPTION) {
        return subscribeToMore({
          document: query,
          updateQuery: (prev, { subscriptionData: { data } }) => {
            const deletedTopic = data?.topic;
            if (
              !prev ||
              !deletedTopic ||
              // it could be that ALL_TOPICS_QUERY failed before or prev is corrupted for some other reason
              // so in order to guard against this, we have to check if the path actually exists,
              // as otherwise we can't remove the topic from the cache anyways
              !_.has(prev, path)
            ) {
              return prev;
            }

            const result = updateApolloList(prev, data, path, 'topic', false);

            return _.update(
              result,
              path,
              (topics: DocumentType<typeof ALL_TOPIC_FIELDS_FRAGMENT>[]) => {
                const parentId = deletedTopic.parent?.id;
                const newParent =
                  (parentId && topics.find((t) => t?.id === parentId)) ?? null;

                return topics.map((topic) => ({
                  ...topic,
                  parent:
                    topic?.parent?.id === deletedTopic?.id
                      ? newParent
                      : topic?.parent,
                }));
              },
            );
          },
        });
      }

      return subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(path, 'topic', true),
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [skip, subscribeToMore]);
};

export const useRefetchAllTopics = () =>
  useLazyQuery(ALL_TOPICS_QUERY, {
    fetchPolicy: 'network-only',
  })[0];

const useAllTopicsQuery = () => {
  const isNonVisitorUser = useIsNonVisitorUser();
  const isPublicView = useIsPublicView();
  const skip = !isNonVisitorUser || isPublicView;

  const userMap = useAllUsersMap();
  const groupMap = useAllGroupMap({ skip });
  const response = useQuery(ALL_TOPICS_QUERY, { skip });

  useSubscribeEffect(response.subscribeToMore, skip);

  const responseTopics = response.data?.session?.organization?.topics;
  return useMemo(() => {
    if (!responseTopics) return null;

    const fullTopics = populateTopic({
      topics: omitNonTopics(responseTopics).toSorted(sortTopic),
      userMap,
      groupMap,
    });
    return {
      redactedTopicIds: new Set(
        responseTopics
          .filter((t) => t.__typename === 'RedactedTopic')
          .map((t) => t.id),
      ),
      topicTreeList: toTopicTreeList(fullTopics),
    };
  }, [responseTopics, userMap, groupMap]);
};

type AllTopicsTreeList = NonNullable<
  ReturnType<typeof useAllTopicsQuery>
>['topicTreeList'];
export type AllTopicsTreeListTopic = AllTopicsTreeList[number];

const AllTopicsContext = createContext<{
  hasLoaded: boolean;
  redactedTopicIds: Set<string>;
  topicTreeList: AllTopicsTreeList;
  flattenedTopicTreeList: AllTopicsTreeList;
  topicMap: Record<string, AllTopicsTreeListTopic>;
}>({
  hasLoaded: false,
  redactedTopicIds: new Set(),
  topicTreeList: [],
  flattenedTopicTreeList: [],
  topicMap: {},
});

export const AllTopicsProvider = ({ children }: { children: ReactNode }) => {
  const result = useAllTopicsQuery();

  const value = useMemo(() => {
    const flattenedTopicTreeList = result
      ? flattenTopicTreeList(result.topicTreeList)
      : [];
    return {
      hasLoaded: !!result,
      redactedTopicIds: result?.redactedTopicIds ?? new Set(),
      topicTreeList: result?.topicTreeList ?? [],
      flattenedTopicTreeList,
      topicMap: _.keyBy(flattenedTopicTreeList, 'id'),
    } satisfies ContextType<typeof AllTopicsContext>;
  }, [result]);

  return (
    <AllTopicsContext.Provider value={value}>
      {children}
    </AllTopicsContext.Provider>
  );
};

interface UseAllTopicsOptions {
  flatten?: boolean;
}

const useAllTopics = ({
  flatten = false,
}: UseAllTopicsOptions = {}): AllTopicsTreeList => {
  const { topicTreeList, flattenedTopicTreeList } =
    useContext(AllTopicsContext);
  return flatten ? flattenedTopicTreeList : topicTreeList;
};

export const useAllTopicMap = () => useContext(AllTopicsContext).topicMap;
export const useRedactedTopicIds = () =>
  useContext(AllTopicsContext).redactedTopicIds;

export const useTopicsHasLoaded = () => useContext(AllTopicsContext).hasLoaded;

export default useAllTopics;
